import * as THREE from 'three';

import { AISLE_WIDTH, BOOTH, NEIGHBOR } from '../config/booth';

/** Hall ceiling height; the Venetian Expo's halls are around this. */
const CEILING = 30;

function carpet(color: string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.95,
    metalness: 0,
  });
}

function slab(
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.receiveShadow = true;
  m.castShadow = true;
  return m;
}

/** A generic neighbouring exhibitor: finished walls, no branding, so ours stands out. */
function genericBooth(
  x: number,
  z: number,
  w: number,
  d: number,
  h: number,
): THREE.Group {
  const g = new THREE.Group();
  const wall = new THREE.MeshStandardMaterial({
    color: '#d9d4c7',
    roughness: 0.8,
  });
  const trim = new THREE.MeshStandardMaterial({
    color: '#56606f',
    roughness: 0.6,
  });
  g.add(slab(w, h, 0.3, wall, x + w / 2, h / 2, z + 0.15));
  g.add(slab(0.3, h, d, wall, x + w - 0.15, h / 2, z + d / 2));
  g.add(slab(w, 0.3, d, trim, x + w / 2, 0.15, z + d / 2));
  g.add(slab(3, 3, 1.5, trim, x + w / 2, 1.5, z + d / 2));
  return g;
}

/** Floor, carpet, neighbours, far-off hall structure and the lighting rig. */
export function buildHall(): THREE.Group {
  const hall = new THREE.Group();

  const aisle = carpet('#2b3140');
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(400, 400), aisle);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(BOOTH.width / 2, -0.01, BOOTH.depth / 2);
  floor.receiveShadow = true;
  hall.add(floor);

  // Our booth carpet, the light grey from the rendering, slightly raised so it reads.
  const ours = new THREE.Mesh(
    new THREE.PlaneGeometry(BOOTH.nominalWidth, BOOTH.nominalDepth),
    carpet('#9ea3ad'),
  );
  ours.rotation.x = -Math.PI / 2;
  ours.position.set(BOOTH.width / 2, 0.005, BOOTH.depth / 2);
  ours.receiveShadow = true;
  hall.add(ours);

  // Booth 44349 (a 10x10) shares our left side, with the rest of the row
  // beyond it; across the main aisle, more exhibitors. The right is open
  // floor to the cross aisle, as on the Hall G floor plan.
  hall.add(
    genericBooth(-NEIGHBOR.width - 0.5, 0, NEIGHBOR.width, BOOTH.depth, 8),
  );
  hall.add(genericBooth(-NEIGHBOR.width - 21.5, 0, 20, BOOTH.depth, 8));
  const across = BOOTH.depth + AISLE_WIDTH;
  for (let i = -3; i < 4; i++)
    hall.add(genericBooth(i * 10.5 - 5, across, 10, 10, 8));

  // The hall perimeter: a wall behind our row and another well to the right,
  // where the floor plan's boundary turns toward the main aisle.
  const perimeter = new THREE.MeshStandardMaterial({
    color: '#3a3d47',
    roughness: 0.95,
  });
  hall.add(slab(220, 14, 0.6, perimeter, BOOTH.width / 2 - 20, 7, -1));
  hall.add(slab(0.6, 14, 120, perimeter, BOOTH.width + 45, 7, 50));

  // Ceiling with truss light strips.
  const ceiling = new THREE.Mesh(
    new THREE.PlaneGeometry(400, 400),
    new THREE.MeshStandardMaterial({ color: '#15181f', roughness: 1 }),
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.set(BOOTH.width / 2, CEILING, BOOTH.depth / 2);
  // Layer 1: overhead-only geometry the plan camera must not see.
  ceiling.layers.set(1);
  hall.add(ceiling);
  const strip = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    emissive: '#fff4e0',
    emissiveIntensity: 1.2,
  });
  for (let i = -6; i <= 6; i++) {
    const s = new THREE.Mesh(new THREE.BoxGeometry(200, 0.3, 0.8), strip);
    s.position.set(BOOTH.width / 2, CEILING - 1, BOOTH.depth / 2 + i * 22);
    s.layers.set(1);
    hall.add(s);
  }

  // Lighting: soft hemisphere for the hall, one shadow-casting key light, booth spots.
  hall.add(new THREE.HemisphereLight('#dfe6ff', '#2a2420', 0.9));
  const key = new THREE.DirectionalLight('#fff6ea', 1.6);
  key.position.set(BOOTH.width / 2 - 14, 28, BOOTH.depth + 22);
  key.target.position.set(BOOTH.width / 2, 0, BOOTH.depth / 2);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.left = -40;
  key.shadow.camera.right = 40;
  key.shadow.camera.top = 40;
  key.shadow.camera.bottom = -40;
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 90;
  key.shadow.bias = -0.0008;
  hall.add(key, key.target);
  for (const x of [3, 10, 16]) {
    const spot = new THREE.SpotLight('#ffffff', 60, 30, Math.PI / 5, 0.5, 1.4);
    spot.position.set(x, 9.6, 1.5);
    spot.target.position.set(x, 0, 5);
    hall.add(spot, spot.target);
  }
  return hall;
}
